import { useContext, type ComponentType } from "react";
import { lazyRetry } from "./lazyRetry";
import { SsrRequestContext } from "./ssr-bootstrap";
import { isKnowledgeObject, knowledgeTemplate, seededKnowledgeData } from "./public-knowledge-template";
import { isLiveLegalContent, publicLegalContent, type LiveLegalContent } from "./public-legal-content";
import { parseLocaleFromPath } from "@/i18n/LocaleContext";
import { BRAND_TRANSITION_PATH } from "./brand-transition-meta";
import { getCanonicalPublicOrigin } from "./canonical-origin";

const registrations = {
  "/apex-score": { page: "apex-score", preferCms: false, load: () => import("@/pages/ApexScorePage") },
  [BRAND_TRANSITION_PATH]: { page: "brand-transition", preferCms: false, load: () => import("@/pages/BrandTransitionPage") },
  "/about": { page: "about", preferCms: true, load: () => import("@/pages/AboutPage") },
  "/how-it-works": { page: "how-it-works", preferCms: true, load: () => import("@/pages/HowItWorksPage") },
  "/privacy": { page: "privacy", preferCms: false, load: () => import("@/pages/PrivacyPage") },
  "/terms": { page: "terms", preferCms: false, load: () => import("@/pages/TermsPage") },
} as const;
type StaticPath = keyof typeof registrations;
type StaticPageView = { page: typeof registrations[StaticPath]["page"]; legalContent?: LiveLegalContent | null };
export function staticPublicPath(pathname: string): StaticPath | null {
  const path = pathname.replace(/\/$/, "");
  if (Object.prototype.hasOwnProperty.call(registrations, path)) return path as StaticPath;
  // Template selection is not publication proof. The server separately checks
  // the complete, own-language information body before publishing this route.
  const locale = parseLocaleFromPath(path);
  return locale.market && ["/about", "/how-it-works"].includes(locale.innerPath)
    ? locale.innerPath as StaticPath : null;
}
export function readPublicStaticSource(pathname: string, options: { legalContent?: unknown } = {}) {
  const path = staticPublicPath(pathname);
  if (!path || pathname.replace(/\/$/, "") !== path) return null;
  const legal = path === "/privacy" || path === "/terms";
  if (legal && !Object.prototype.hasOwnProperty.call(options, "legalContent"))
    throw Object.assign(Error("Required legal source snapshot absent"), { status: 503 });
  const publicTemplate = knowledgeTemplate("static-page", path, {
    page: registrations[path].page, ...(legal ? { legalContent: publicLegalContent(options.legalContent) } : {}),
  });
  return {
    sourceReference: publicTemplate.sourceReference, preferCms: registrations[path].preferCms,
    row: { kind: "static", contentIdentity: path === "/privacy" ? "authored:privacy" : publicTemplate.sourceReference, canonicalPath: path,
      ...([BRAND_TRANSITION_PATH, "/apex-score"].includes(path) ? { origin: getCanonicalPublicOrigin() } : {}),
      publication: "published", indexable: true, language: "en", languageVerified: true },
    viewModel: { kind: "compiled", publicTemplate },
  };
}
export function prefersStaticPublicTemplate(pathname: string, {
  seed, decision,
}: { seed?: unknown; decision?: unknown }): boolean {
  const path = staticPublicPath(pathname);
  if (!path) return false;
  if (isKnowledgeObject(seed) && seed.kind === "compiled") return true;
  if (isKnowledgeObject(seed) && seed.kind === "landing") return false;
  if (isKnowledgeObject(decision) && decision.pathname === pathname &&
    decision.kind === "page" && decision.status === 200 &&
    decision.contentIdentity === (path === "/privacy" ? "authored:privacy" : `static-page:${pathname.replace(/\/$/, "")}`)) return true;
  return !registrations[path].preferCms;
}
const components: Partial<Record<StaticPath, ComponentType>> = {};
const pending: Partial<Record<StaticPath, Promise<void>>> = {};
const lazyPages = {
  "/apex-score": lazyRetry(registrations["/apex-score"].load),
  [BRAND_TRANSITION_PATH]: lazyRetry(registrations[BRAND_TRANSITION_PATH].load),
  "/about": lazyRetry(registrations["/about"].load),
  "/how-it-works": lazyRetry(registrations["/how-it-works"].load),
  "/privacy": lazyRetry(registrations["/privacy"].load),
  "/terms": lazyRetry(registrations["/terms"].load),
};
export function loadEagerStaticPage(path: StaticPath): Promise<void> {
  if (!pending[path]) pending[path] = registrations[path].load().then(module => {
    components[path] = module.default;
  }).catch(error => { delete pending[path]; throw error; });
  return pending[path]!;
}
export function PublicStaticRoute({ pathname }: { pathname: StaticPath }) {
  const request = useContext(SsrRequestContext);
  seededKnowledgeData<StaticPageView>(request?.seed, "static-page", pathname,
    (data): data is StaticPageView => isKnowledgeObject(data) && data.page === registrations[pathname].page &&
      (!["/privacy", "/terms"].includes(pathname) || Object.prototype.hasOwnProperty.call(data, "legalContent") &&
        (data.legalContent === null || isLiveLegalContent(data.legalContent))));
  const Page = components[pathname] || lazyPages[pathname];
  return <Page />;
}
